import { Layer, Line, Rect, Group, Circle, Text } from "react-konva";
import { useEffect, useMemo, useState } from "react";
import type { KonvaEventObject } from "konva/lib/Node";
import type { Vec2, Unit } from "../../lib/types";
import type { FitInfo } from "../../lib/useFitToRoom";
import { distance, formatLength, midpoint, snapVecTo } from "../../lib/geom";
import { annotationScale, chromeScale } from "../../lib/canvasLabels";

interface Props {
  width: number;
  height: number;
  fit: FitInfo;
  unit: Unit;
  snapEnabled: boolean;
  /** Room-grid anchor (top-left of room bounding box). */
  gridOrigin: Vec2;
  /** Grid spacing in inches (matches RoomCanvas). */
  gridInches: number;
  /** Stage zoom, so the label and endpoints keep a steady on-screen size. */
  stageScale?: number;
}

const ENDPOINT_RADIUS_PX = 5;
const LINE_COLOR = "#2a70f5";
const LABEL_BG = "#0c0c10";

export function MeasureOverlay({
  width,
  height,
  fit,
  unit,
  snapEnabled,
  gridOrigin,
  gridInches,
  stageScale = 1,
}: Props) {
  const [points, setPoints] = useState<Vec2[]>([]);
  const labelK = annotationScale(stageScale);
  const dotR = ENDPOINT_RADIUS_PX * chromeScale(stageScale);
  const [hoverRoom, setHoverRoom] = useState<Vec2 | null>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setPoints([]);
        setHoverRoom(null);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  function roomFromPointer(e: KonvaEventObject<unknown>): Vec2 | null {
    const stage = e.target.getStage();
    if (!stage) return null;
    const p = stage.getRelativePointerPosition();
    if (!p) return null;
    let room = fit.pxToRoom(p);
    if (snapEnabled) {
      room = snapVecTo(room, gridInches, gridOrigin);
    }
    return room;
  }

  function placePoint(e: KonvaEventObject<unknown>) {
    e.cancelBubble = true;
    const room = roomFromPointer(e);
    if (!room) return;
    setPoints((prev) => {
      if (prev.length === 0) return [room];
      if (prev.length === 1) return [prev[0], room];
      return [room];
    });
  }

  function updateHover(e: KonvaEventObject<unknown>) {
    if (points.length !== 1) return;
    e.cancelBubble = true;
    setHoverRoom(roomFromPointer(e));
  }

  function clearHover() {
    setHoverRoom(null);
  }

  const segmentEnd = points.length === 2 ? points[1] : hoverRoom;
  const distIn =
    points.length >= 1 && segmentEnd
      ? distance(points[0], segmentEnd)
      : null;

  const visuals = useMemo(() => {
    if (points.length === 0) return null;
    const aPx = fit.pointToPx(points[0]);
    const bRoom = points.length === 2 ? points[1] : hoverRoom;
    if (!bRoom) {
      return { aPx, line: null as number[] | null, bPx: null, midPx: null, label: null as string | null };
    }
    const bPx = fit.pointToPx(bRoom);
    const midRoom = midpoint(points[0], bRoom);
    const midPx = fit.pointToPx(midRoom);
    const label =
      distIn != null && points.length === 2
        ? formatLength(distIn, unit)
        : distIn != null
          ? formatLength(distIn, unit)
          : null;
    return {
      aPx,
      bPx,
      line: [aPx.x, aPx.y, bPx.x, bPx.y],
      midPx,
      label,
    };
  }, [points, hoverRoom, fit, distIn, unit]);

  const showFinalLabel = points.length === 2 && visuals?.label;

  return (
    <Layer>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="rgba(0,0,0,0.001)"
        listening
        onMouseDown={placePoint}
        onMouseMove={updateHover}
        onMouseLeave={clearHover}
        onTouchStart={placePoint}
        onTouchMove={updateHover}
        onTouchEnd={clearHover}
      />

      {visuals?.aPx && (
        <Circle
          x={visuals.aPx.x}
          y={visuals.aPx.y}
          radius={dotR}
          fill={LINE_COLOR}
          stroke="#ffffff"
          strokeWidth={2}
          strokeScaleEnabled={false}
          listening={false}
        />
      )}
      {visuals?.bPx && (
        <Circle
          x={visuals.bPx.x}
          y={visuals.bPx.y}
          radius={dotR}
          fill={LINE_COLOR}
          stroke="#ffffff"
          strokeWidth={2}
          strokeScaleEnabled={false}
          listening={false}
        />
      )}
      {visuals?.line && (
        <Line
          points={visuals.line}
          stroke={LINE_COLOR}
          strokeWidth={2}
          strokeScaleEnabled={false}
          dash={points.length === 2 ? undefined : [6, 4]}
          listening={false}
        />
      )}
      {showFinalLabel && visuals.midPx && visuals.label && (
        <Group x={visuals.midPx.x} y={visuals.midPx.y} scaleX={labelK} scaleY={labelK}>
          <MeasureLabel text={visuals.label} />
        </Group>
      )}
      {points.length === 1 && visuals?.midPx && visuals.label && (
        <Group x={visuals.midPx.x} y={visuals.midPx.y} scaleX={labelK} scaleY={labelK}>
          <MeasureLabel text={visuals.label} dimmed />
        </Group>
      )}
    </Layer>
  );
}

function MeasureLabel({ text, dimmed = false }: { text: string; dimmed?: boolean }) {
  const charW = 7;
  const textWidth = text.length * charW + 14;
  return (
    <>
      <Rect
        x={-textWidth / 2}
        y={-11}
        width={textWidth}
        height={22}
        fill={LABEL_BG}
        cornerRadius={11}
        opacity={dimmed ? 0.85 : 1}
      />
      <Text
        text={text}
        x={-textWidth / 2}
        y={-7}
        width={textWidth}
        align="center"
        fontSize={12}
        fontStyle="600"
        fontFamily="Inter, sans-serif"
        fill="#ffffff"
        opacity={dimmed ? 0.9 : 1}
      />
    </>
  );
}
